import { useAuthStore } from '../stores/authStore';
import { readSSEData } from '@agente-gerencial/shared';

const API_BASE = import.meta.env?.VITE_API_BASE || 'http://localhost:8787';

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = useAuthStore.getState().token;
  const headers = new Headers(options.headers);
  headers.set('Content-Type', 'application/json');
  if (token) headers.set('X-User-Token', token);

  const response = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (response.status === 401) {
    useAuthStore.getState().clearToken();
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  if (response.status === 204) return undefined as T;
  return response.json();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  async stream(path: string, body: unknown, onChunk: (chunk: string) => void): Promise<void> {
    const token = useAuthStore.getState().token;
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-User-Token': token || '' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Stream failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    if (!response.body) throw new Error('Stream failed');

    let hasContent = false;
    for await (const data of readSSEData(response.body)) {
      if (data.trim() === '[DONE]') {
        if (!hasContent) throw new Error('A IA não retornou uma resposta. Tente novamente.');
        return;
      }
      let chunk;
      try { chunk = JSON.parse(data); } catch { throw new Error('Resposta de streaming inválida.'); }
      if (chunk.error) throw new Error(chunk.error);
      if (typeof chunk.content !== 'string') throw new Error('Resposta de streaming inválida.');
      if (chunk.content.trim()) hasContent = true;
      onChunk(chunk.content);
    }
    throw new Error('A resposta foi interrompida antes de concluir. Tente novamente.');
  },
};
